import { IBuildings, ICityRegions } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrictV2 } from '@/shared/interfaces/store-base';
import { buildRegionAssignmentPayload, ICityPageRegionAssignmentChange } from '@/shared';
import cities from '@/shared/services/cities';
import properties from '@/shared/services/properties';

/** Adds the city page property list that region assignment is driven from. @see docs/RSX-6008-region-management.md */
interface IRegionsState extends IStateStrictV2<ICityRegions> {
  cityPageProperties: IBuildings[];
}

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/regions';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  select: ['id', 'regionName', 'cityoffered.clientId'],
  relations: [
    {
      path: 'cityoffered',
      alias: 'cityoffered',
    },
  ],
};

const defaultGetSingleFilters: IQueryCommandOptions = {};

const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'regions',
  keys: ['regionName'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ICityRegions>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'regions/'

  state: {
    ...base.state,
    cityPageProperties: [] as IBuildings[],
  },

  getters: {
    ...base.getters,

    cityPageProperties: (state: IRegionsState): IBuildings[] => [...state.cityPageProperties],
  },

  mutations: {
    ...base.mutations,

    setCityPageProperties: (state: IRegionsState, payload: IBuildings[]) => {
      state.cityPageProperties = payload;
    },

    resetCityPageProperties: (state: IRegionsState) => {
      state.cityPageProperties = [];
    },

    /**
     * Applies saved region assignments to the loaded property list.
     *
     * Only `regionId` is updated — the `region` relation beside it is left as loaded. It serves purely
     * as a lookup for naming regions that belong to other city pages, and a stale entry there names a
     * region nothing points at any more rather than mislabelling anything.
     */
    setPropertyRegionIds: (state: IRegionsState, changes: ICityPageRegionAssignmentChange[]) => {
      const regionIdsByPropertyId = new Map(changes.map((change) => [change.propertyId, change.toRegionId]));

      state.cityPageProperties = state.cityPageProperties.map((building) => (
        building.id && regionIdsByPropertyId.has(building.id)
          ? { ...building, regionId: regionIdsByPropertyId.get(building.id) ?? null }
          : building
      ));
    },
  },

  actions: {
    ...base.actions,

    /**
     * Loads the regions belonging to one city page, ordered by name.
     *
     * Kept as its own action rather than using the base `get` because the endpoint needs the `siteId`
     * query param for site-level access checks, which the query command builder does not carry.
     * @param {object} payload - identifies the city page
     * @param {number} payload.cityOfferedId - `cities_offered.id` of the city page being edited
     */
    getByCityPage: async (ctx: IAction<IRegionsState>, { cityOfferedId }: { cityOfferedId: number }) => {
      const result = await cities.getRegionsByCityOfferedAndClient(cityOfferedId);
      ctx.commit('set', result);
      return result;
    },

    /**
     * Loads the properties in a city that a city page's regions can be assigned to, keeping the raw
     * records so callers can apply their own site scoping and per-property permission filtering.
     * @param {object} payload - identifies the city
     * @param {number} payload.cityId - `geo_cities.id` shared by the city page and its properties
     */
    getCityPageProperties: async (ctx: IAction<IRegionsState>, { cityId }: { cityId: number }) => {
      const result = await properties.getPropertiesByCityForRegions(cityId);
      ctx.commit('setCityPageProperties', result.data ?? []);
      return result.data ?? [];
    },

    /**
     * Persists region assignments for a set of properties, then applies them to the loaded list.
     *
     * Payloads are built from the rows already held in `cityPageProperties`, the way the property list
     * page saves the row it loaded rather than re-reading it. `properties.saveMany` is a full-object PUT,
     * so the row has to be complete — which is why `getPropertiesByCityForRegions` selects no subset.
     * Note that `saveMany` fans out to one PUT per property rather than issuing a single request.
     *
     * Throws if a property is not in the loaded list or fails to save, leaving the caller to revert its
     * optimistic state.
     * @param {object} payload - the moves to persist
     * @param {ICityPageRegionAssignmentChange[]} payload.changes - one entry per property whose region changes
     */
    savePropertyRegionAssignments: async (
      ctx: IAction<IRegionsState>,
      { changes }: { changes: ICityPageRegionAssignmentChange[] },
    ) => {
      if (changes.length === 0) return;

      const loadedPropertiesById = new Map<number, IBuildings>(
        ctx.getters.cityPageProperties.flatMap((building: IBuildings) => (building.id ? [[building.id, building]] : [])),
      );

      const payloads = changes.map((change): IBuildings => {
        const property = loadedPropertiesById.get(change.propertyId);

        if (!property) {
          throw new Error(`Property ${change.propertyId} is not loaded, so its region cannot be saved.`);
        }

        return buildRegionAssignmentPayload(property, change.toRegionId);
      });

      await properties.saveMany(payloads);
      ctx.commit('setPropertyRegionIds', changes);
    },
  },
};
